import { useEffect, useState } from 'react';

// Height of the bar; must match TITLE_BAR_HEIGHT in echo_desktop/src/main/constants.js.
export const DESKTOP_TITLE_BAR_HEIGHT = 36;

const FALLBACK_BG = '#141415';

// Mirrors EchoUpdateState in echo_desktop/types/global.d.ts.
type UpdateState = { status: 'idle' | 'downloading' | 'ready'; version: string | null; percent: number };

function toHex(cssColor: string): string | null {
  const m = cssColor.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (!m) return null;
  return '#' + [m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, '0')).join('');
}

function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  return 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
}

// The native min/max/close buttons are drawn by the OS over a flat colour, so they follow the resolved page background
// (including the tinted theme, whose --app-bg is a gradient start) and flip their icons on light backgrounds.
function syncWindowControls() {
  const bg = toHex(getComputedStyle(document.body).backgroundColor) || FALLBACK_BG;
  const symbol = luminance(bg) > 150 ? '#3f3f46' : '#a3a3a3';
  (window as any).echoDesktop?.setTitleBarColors?.(bg, symbol);
}

// The desktop shell downloads updates in the background; this mirrors its state so the bar can offer a one-click restart.
function useDesktopUpdate(enabled: boolean): UpdateState {
  const [state, setState] = useState<UpdateState>({ status: 'idle', version: null, percent: 0 });
  useEffect(() => {
    if (!enabled) return;
    const bridge = (window as any).echoDesktop;
    let alive = true;
    bridge?.getUpdateState?.().then((s: UpdateState) => { if (alive && s) setState(s); }).catch(() => {});
    const off = bridge?.onUpdateState?.((s: UpdateState) => setState(s));
    return () => { alive = false; off?.(); };
  }, [enabled]);
  return state;
}

function UpdateButton({ update }: { update: UpdateState }) {
  if (update.status === 'idle') return null;
  if (update.status === 'downloading') {
    // A quiet progress ring: informational only, nothing to click yet.
    const r = 6;
    const c = 2 * Math.PI * r;
    return (
      <span
        className="absolute right-3 flex items-center text-fg-2"
        title={`Downloading update… ${update.percent}%`}
        aria-label={`Downloading update, ${update.percent} percent`}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="-rotate-90">
          <circle cx="8" cy="8" r={r} stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
          <circle cx="8" cy="8" r={r} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - update.percent / 100)} />
        </svg>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => (window as any).echoDesktop?.installUpdate?.()}
      title={`Update ready${update.version ? ` (${update.version})` : ''}: click to restart Echo`}
      aria-label="Update ready: restart Echo to install"
      className="absolute right-2 flex h-6 items-center gap-1.5 rounded-full px-2.5 text-[0.6875rem] font-semibold transition-opacity hover:opacity-85 active:opacity-70"
      style={{ background: 'var(--echo-accent, #10b981)', color: 'var(--echo-accent-fg, #ffffff)', WebkitAppRegion: 'no-drag' } as React.CSSProperties}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 4v11m0 0-4-4m4 4 4-4M5 20h14" />
      </svg>
      Update
    </button>
  );
}

// Drag handle and window title for the Electron desktop app, in place of the native title bar. Renders nothing elsewhere.
function DesktopTitleBar({ title }: { title: string }) {
  const isDesktop = Boolean((window as any).echoDesktop);
  const update = useDesktopUpdate(isDesktop);

  useEffect(() => {
    if (!isDesktop) return;
    syncWindowControls();
    const obs = new MutationObserver(syncWindowControls);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bg', 'data-theme', 'style'] });
    return () => obs.disconnect();
  }, [isDesktop]);

  if (!isDesktop) return null;
  return (
    <div
      className="fixed top-0 z-[6000] flex items-center justify-center select-none brand-app-bg"
      style={{
        left: 'env(titlebar-area-x, 0px)',
        width: 'env(titlebar-area-width, 100%)',
        height: DESKTOP_TITLE_BAR_HEIGHT,
        // @ts-expect-error -- non-standard Chromium property for draggable window regions
        WebkitAppRegion: 'drag',
      }}
    >
      <img src="/brand/icon-192.png" alt="" width={16} height={16} className="absolute left-3 rounded-[4px] opacity-90" draggable={false} />
      <span className="max-w-[60%] truncate text-[0.75rem] font-medium tracking-[0.01em] text-fg-2">{title}</span>
      <UpdateButton update={update} />
    </div>
  );
}

export default DesktopTitleBar;
